import { useMutation } from '@tanstack/react-query'
import { useMatches } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

import type { FrontEndTrackingMessage } from '@orus.eu/backend'
import { isFailure } from '@orus.eu/result'

import { useTRPC } from '../../client.ts'
import { isProductionEnvironment } from '../env-utils.ts'
import { assert } from '../errors.ts'
import { ExclusiveOperationsQueue } from '../exclusive-operations-queue.ts'
import { logger } from '../logger.ts'
import { getPostHogAnonymousId, getPostHogDistinctId, getPostHogSessionId } from '../posthog-utils.ts'
import { serializeError } from '../serialize-error.ts'

import { sendMetaViewedEvent, sendTrackingEventToMeta } from './meta-tracking.ts'
import { getPageNameAndResourceIdFromRoute, type PathParamName, type RouteId } from './page-identification.ts'

/**
 * Creates a canonical route similar to PostHog's $current_url
 * Includes full URL with host but removes sensitive parameters
 * This is like $current_url - includes dynamic IDs for detailed tracking,
 * while removing sensitive parameters and hash/fragment to protect PII.
 */
function computeCanonicalRoute(fullUrl: string): string {
  const u = new URL(fullUrl)

  // Remove sensitive parameters
  const sensitiveParams = ['token', 'email', 'session', 'jwt', 'password', 'key', 'secret', 'auth']
  sensitiveParams.forEach((param) => u.searchParams.delete(param))

  // Remove hash/fragment
  u.hash = ''

  // Return full URL (like PostHog's $current_url)
  return u.toString()
}

/**
 * Creates a pathname similar to PostHog's $pathname
 * Just the path portion without host or query parameters
 */
function computePathname(fullUrl: string): string {
  const u = new URL(fullUrl)
  let path = u.pathname.replace(/\/+$/, '')
  if (path === '') path = '/'
  return path
}

/**
 * Creates a low-cardinality path for screen/page analysis
 * Normalizes dynamic IDs to create route patterns for analytics
 */
function computeLowCardinalityPath(fullUrl: string): string {
  const u = new URL(fullUrl)
  let path = u.pathname.replace(/\/+$/, '')
  if (path === '') path = '/'

  // Normalize common dynamic patterns to reduce cardinality
  path = path
    // Subscription IDs: /subscribe/123/activity -> /subscribe/$subscriptionId/$stepId
    .replace(/^\/subscribe\/[^/]+\/[^/]+/, '/subscribe/$subscriptionId/$stepId')
    // Token-based routes: /s/e-v2/abc123 -> /s/e-v2/$token
    .replace(/^\/s\/e-v2\/[^/]+/, '/s/e-v2/$token')
    .replace(/^\/s\/e\/[^/]+/, '/s/e/$token')
    .replace(/^\/s\/r\/[^/]+/, '/s/r/$token')
    .replace(/^\/s\/q\/[^/]+/, '/s/q/$token')
    .replace(/^\/s\/c\/[^/]+/, '/s/c/$token')
    .replace(/^\/s\/hf\/[^/]+/, '/s/hf/$token')
    // Partner routes: /partner/org123 -> /partner/$organization
    .replace(/^\/partner\/[^/]+/, '/partner/$organization')
    // Backoffice subscription details: /bo/subscriptions/123 -> /bo/subscriptions/$subscriptionId
    .replace(/^\/bo\/subscriptions\/[^/]+/, '/bo/subscriptions/$subscriptionId')
    // Backoffice subscription steps: /bo/subscriptions/123/step456 -> /bo/subscriptions/$subscriptionId/$stepId
    .replace(/^\/bo\/subscriptions\/[^/]+\/[^/]+/, '/bo/subscriptions/$subscriptionId/$stepId')
    // Backoffice users: /bo/users/123 -> /bo/users/$userId
    .replace(/^\/bo\/users\/[^/]+/, '/bo/users/$userId')
    // Backoffice contracts: /bo/contracts/123 -> /bo/contracts/$subscriptionId
    .replace(/^\/bo\/contracts\/[^/]+/, '/bo/contracts/$subscriptionId')
    // Backoffice invoices: /bo/invoicing/123 -> /bo/invoicing/$invoiceId
    .replace(/^\/bo\/invoicing\/[^/]+/, '/bo/invoicing/$invoiceId')
    // Customer routes: /contract/123 -> /contract/$subscriptionId
    .replace(/^\/contract\/[^/]+/, '/contract/$subscriptionId')
    // Insurance certificate generators
    .replace(
      /^\/insurance-certificate-generator-real-estate-agents\/[^/]+/,
      '/insurance-certificate-generator-real-estate-agents/$subscriptionId',
    )
    .replace(
      /^\/insurance-certificate-generator-real-estate-sales-agents\/[^/]+/,
      '/insurance-certificate-generator-real-estate-sales-agents/$subscriptionId',
    )

  return path
}

/**
 * Creates a host similar to PostHog's $host
 * Just the domain (and port if present) without protocol or path
 */
function computeHost(fullUrl: string): string {
  const u = new URL(fullUrl)
  return u.host
}

export function safeStorageGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

export function safeStorageSetItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    // Silently ignore storage access issues
  }
}

export function safeStorageKeys(storage: Storage): string[] {
  try {
    return Object.keys(storage)
  } catch {
    return []
  }
}

export function getSafeStorage(storageType: 'local' | 'session'): Storage | null {
  try {
    return storageType === 'local' ? localStorage : sessionStorage
  } catch {
    return null
  }
}

// Memoize fallback IDs so that when storage is unavailable,
// the same ID is returned for the entire page lifecycle (prevents phantom MTUs)
const inMemoryFallbackIds = new Map<string, string>()

function generateFallbackId(prefix: string): string {
  const cached = inMemoryFallbackIds.get(prefix)
  if (cached) return cached

  const globalCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  let id: string
  if (globalCrypto?.randomUUID) {
    id = `${prefix}_${globalCrypto.randomUUID()}`
  } else if (typeof globalCrypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    globalCrypto.getRandomValues(bytes)
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
    id = `${prefix}_${hex}`
  } else {
    id = `${prefix}_${Math.random().toString(36).slice(2)}`
  }

  inMemoryFallbackIds.set(prefix, id)
  return id
}

function getOrCreateFallbackId({
  storage,
  storageKey,
  prefix,
}: {
  storage: Storage
  storageKey: string
  prefix: string
}): string {
  const existing = safeStorageGetItem(storage, storageKey)
  if (existing) {
    return existing
  }
  const fallback = generateFallbackId(prefix)
  safeStorageSetItem(storage, storageKey, fallback)
  return fallback
}

/**
 * Fields that are automatically added to tracking messages by createTrackingMessage
 */
type AutoAddedFields = {
  posthog_session_id: string | null
  posthog_distinct_id: string | null
  posthog_anonymous_id: string | null
  canonical_route: string
  pathname: string
  route_pattern: string
  host: string
  // See `docs/identity-reconciliation.md` > "Timestamp Accuracy".
  client_timestamp_ms: number
}

/**
 * Helper type that distributes Omit over union types
 * This ensures TypeScript properly validates each member of the FrontEndTrackingMessage union
 */
type TrackingMessageWithoutAutoFields<T> = T extends infer U ? Omit<U, keyof AutoAddedFields> : never

/**
 * Helper function to create tracking messages with PostHog fields and route information automatically injected
 * This follows recommendations to avoid repetitive null assignments
 *
 * This function is strictly typed to ensure the input message (without auto-added fields) is a valid FrontEndTrackingMessage.
 *
 * We use a satisfies-style check by assigning to a const with the exact type. This forces TypeScript
 * to validate the entire object structure at the call site, including the discriminated 'event' field.
 */
export function createTrackingMessage<T extends FrontEndTrackingMessage>(
  message: TrackingMessageWithoutAutoFields<T>,
): T {
  // This assignment forces TypeScript to validate 'message' matches the exact type
  // If the event field or any other field doesn't match, this will fail
  // This is more strict than just using the parameter type because it forces
  // TypeScript to check the assignment, not just parameter compatibility
  const _validated: TrackingMessageWithoutAutoFields<T> = message
  const sessionStorageSafe = getSafeStorage('session')
  const localStorageSafe = getSafeStorage('local')
  const posthogSessionId =
    getPostHogSessionId() ??
    (sessionStorageSafe
      ? getOrCreateFallbackId({
          storage: sessionStorageSafe,
          storageKey: 'orus_fallback_posthog_session_id',
          prefix: 'fallback_session',
        })
      : generateFallbackId('fallback_session'))
  const posthogDistinctId =
    getPostHogDistinctId() ??
    (localStorageSafe
      ? getOrCreateFallbackId({
          storage: localStorageSafe,
          storageKey: 'orus_fallback_posthog_distinct_id',
          prefix: 'fallback_distinct',
        })
      : generateFallbackId('fallback_distinct'))
  const posthogAnonymousId =
    getPostHogAnonymousId() ??
    (localStorageSafe
      ? getOrCreateFallbackId({
          storage: localStorageSafe,
          storageKey: 'orus_fallback_posthog_anonymous_id',
          prefix: 'fallback_anonymous',
        })
      : generateFallbackId('fallback_anonymous'))

  return {
    ..._validated,
    posthog_session_id: posthogSessionId,
    posthog_distinct_id: posthogDistinctId,
    posthog_anonymous_id: posthogAnonymousId,
    canonical_route: computeCanonicalRoute(window.location.href),
    pathname: computePathname(window.location.href),
    route_pattern: computeLowCardinalityPath(window.location.href),
    host: computeHost(window.location.href),
    // eslint-disable-next-line no-restricted-syntax -- client wall clock is intentional, see `docs/identity-reconciliation.md` > "Timestamp Accuracy"
    client_timestamp_ms: Date.now(),
  } as T
}

export function useTracking(ignoredRouteIds: Set<string>): void {
  const trpc = useTRPC()
  const { mutateAsync: sendTrackingMessage } = useMutation(trpc.tracking.sendTrackingMessage.mutationOptions())

  useEffect(() => {
    backendTrackingSender = (message) => sendTrackingMessage(message)
    void flushPendingBackendTrackingMessages()
    return () => {
      backendTrackingSender = null
    }
  }, [sendTrackingMessage])

  const leafMatch = useMatches({
    select: (matches) => matches.at(-1),
  })
  const lastPageViewRef = useRef<{ pathname: string; updatedAt: number } | undefined>(undefined)

  useEffect(() => {
    const alreadyTracked =
      lastPageViewRef.current &&
      leafMatch &&
      lastPageViewRef.current.updatedAt === leafMatch.updatedAt &&
      lastPageViewRef.current.pathname === leafMatch.pathname

    if (
      !leafMatch ||
      leafMatch.globalNotFound ||
      leafMatch.status !== 'success' ||
      ignoredRouteIds.has(leafMatch.id) ||
      alreadyTracked
    ) {
      return
    }

    lastPageViewRef.current = {
      pathname: leafMatch.pathname,
      updatedAt: leafMatch.updatedAt,
    }
    handlePageView({
      routeId: leafMatch.routeId,
      path: leafMatch.pathname,
      params: leafMatch.params,
    })
  }, [leafMatch, ignoredRouteIds])
}

export function sendMessage(message: FrontEndTrackingMessage): void {
  // PostHog fields are already injected by createTrackingMessage helper
  sendMessageToBackend(message)
  sendMessageToGTM(message)
  sendTrackingEventToMeta(message)
}

/**
 * Wait for all pending tracking messages to be sent to the backend
 */
export async function waitForTrackingQueueToBeEmpty(): Promise<void> {
  await backendTrackingQueue.waitForEmpty()
}

function handlePageView({
  routeId,
  path,
  params,
}: {
  routeId: RouteId
  path: string
  params: Partial<Record<PathParamName, string>>
}): void {
  const parsingResult = getPageNameAndResourceIdFromRoute({ routeId, params })
  if (isFailure(parsingResult)) {
    // cannot send the tracking event when the parsing has failed
    return
  }
  const { pageName, resourceId } = parsingResult.output

  sendMetaViewedEvent({
    event: 'viewed',
    name: pageName,
    resourceId,
    title: document.title,
    url: path,
  })
}

const backendTrackingQueue = new ExclusiveOperationsQueue()

let backendTrackingSender: ((message: FrontEndTrackingMessage) => Promise<void>) | null = null
const pendingBackendTrackingMessages: FrontEndTrackingMessage[] = []

async function flushPendingBackendTrackingMessages(): Promise<void> {
  if (!backendTrackingSender) return
  while (pendingBackendTrackingMessages.length > 0) {
    const message = pendingBackendTrackingMessages.shift()
    if (!message) return
    try {
      await backendTrackingSender(message)
    } catch (err: unknown) {
      logger.warning('Error while sending pending tracking message to backend', serializeError(err))
    }
  }
}

function sendMessageToBackend(message: FrontEndTrackingMessage) {
  backendTrackingQueue
    .run(async () => {
      if (!backendTrackingSender) {
        pendingBackendTrackingMessages.push(message)
        return
      }
      await backendTrackingSender(message)
    })
    .catch((err: unknown) => {
      logger.warning('Error while sending tracking message to backend', serializeError(err))
    })
}

function sendMessageToGTM(message: FrontEndTrackingMessage): void {
  const event = message['event']
  const { event: _, ...properties } = message

  // The typescript magic we do for tracking message combined by the type transformation
  // done by trpc, gives a type that's wider than a string.
  // I didn't fully understand it, but I guess we've hit an edge case.
  // So we need this assertion to keep the code compiling
  assert(typeof event === 'string', 'event must be a string')

  const eventName: string = event

  const gtmEvent = { event: eventName, ...properties }

  if (isProductionEnvironment()) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(gtmEvent)
  } else {
    logger.info('In production, we would have sent this event to GTM: ', {
      event: JSON.stringify(gtmEvent),
    })
  }
}
